import { useEffect, useRef, useState } from "react"
import { previewScriptFusion } from "~/api/staff"
import { useDefaultVideoOptions } from "~/store/session"
import { useHotVideoStore } from "~/store/hotVideo"
import { useScriptSaveNavigation } from "~/pages/VideoRecreate/hooks"

type ImportState =
  | { status: "empty" }
  | { status: "loading"; message: string }
  | { status: "ready" }
  | { status: "error"; message: string }

export const useFusionImport = (fusionId: string) => {
  useScriptSaveNavigation()
  const { videoOptions } = useDefaultVideoOptions()
  const optionsRef = useRef(videoOptions)
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState<ImportState>({ status: "empty" })

  useEffect(() => {
    optionsRef.current = videoOptions
    useHotVideoStore.getState().setVideoOptions(videoOptions, "auto")
  }, [videoOptions])

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller
    const store = useHotVideoStore.getState()
    store.clearTaskId()
    store.resetDraftFields()
    store.setVideoOptions(optionsRef.current, "auto")
    const load = async (): Promise<void> => {
      if (!fusionId) {
        setState({ status: "empty" })
        return
      }
      setState({ status: "loading", message: "正在读取融合记录…" })
      try {
        if (
          !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(
            fusionId,
          )
        )
          throw new Error("融合记录 ID 格式不正确，请检查跳转链接")
        const response = await previewScriptFusion(
          { fusion_id: fusionId },
          signal,
        )
        signal.throwIfAborted()
        if (!response.data) throw new Error("未读取到融合预览，请重试")
        useHotVideoStore.getState().importDraft(response.data)
        setState({ status: "ready" })
      } catch (error) {
        if (signal.aborted) return
        setState({
          status: "error",
          message:
            error instanceof Error ? error.message : "导入失败，请稍后重试",
        })
      }
    }
    void load()
    return () => {
      controller.abort()
      const current = useHotVideoStore.getState()
      current.clearTaskId()
      current.resetDraftFields()
    }
  }, [fusionId, attempt])

  return { state, retry: () => setAttempt((value) => value + 1) }
}
